import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, formatCOP, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import N0LeadCapture from "../components/N0LeadCapture";
import ROICalculator from "../components/ROICalculator";
import EntregablesPanel from "../components/EntregablesPanel";
import CaseFlowStepper from "../components/CaseFlowStepper";
import CaseSummaryCard from "../components/CaseSummaryCard";
import FlowGuide from "../components/FlowGuide";
import AiActivityTimeline from "../components/AiActivityTimeline";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

// RF-10 (iter27a): tres etiquetas canónicas de información (sin "Posiblemente apto").
// `information_status` viene del backend; si no viene, se deriva de `result`.
const INFORMATION_STATUS = {
  informacion_pendiente: {
    label: "Información pendiente",
    short: "Necesitamos completar datos antes de avanzar",
    pill: "bg-gray-100 text-gray-800 border-gray-300",
    accent: "border-l-gray-400",
    testid: "info-status-pendiente",
  },
  lista_para_diagnostico: {
    label: "Lista para diagnóstico",
    short: "El caso cumple los datos mínimos para avanzar",
    pill: "bg-emerald-50 text-emerald-800 border-emerald-300",
    accent: "border-l-emerald-500",
    testid: "info-status-lista",
  },
  requiere_revision: {
    label: "Requiere revisión",
    short: "Un experto humano debe revisar este caso",
    pill: "bg-amber-50 text-amber-900 border-amber-300",
    accent: "border-l-amber-500",
    testid: "info-status-revision",
  },
};

function deriveInformationStatus(result) {
  if (result === "datos_insuficientes") return "informacion_pendiente";
  if (result === "apto") return "lista_para_diagnostico";
  return "requiere_revision"; // posiblemente_apto | no_apto | out_of_scope | otros
}

const RESULT_STYLES = {
  apto: {
    label: "Lista para diagnóstico",
    pill: "bg-emerald-50 text-emerald-800 border-emerald-300",
    accent: "border-l-emerald-500",
    testid: "result-apto",
  },
  posiblemente_apto: {
    label: "Requiere revisión",
    pill: "bg-amber-50 text-amber-900 border-amber-300",
    accent: "border-l-amber-500",
    testid: "result-requiere-revision",
  },
  no_apto: {
    label: "Requiere revisión",
    pill: "bg-amber-50 text-amber-900 border-amber-300",
    accent: "border-l-amber-500",
    testid: "result-no-apto",
  },
  out_of_scope: {
    label: "Requiere revisión",
    pill: "bg-indigo-50 text-indigo-800 border-indigo-300",
    accent: "border-l-indigo-500",
    testid: "result-out-of-scope",
  },
  datos_insuficientes: {
    label: "Información pendiente",
    pill: "bg-gray-100 text-gray-800 border-gray-300",
    accent: "border-l-gray-400",
    testid: "result-datos-insuficientes",
  },
};

const FLAG_LABELS = {
  app: "APP / Asociación público privada",
  concesion: "Concesión",
  regimen_especial_complejo: "Régimen especial complejo",
  urgencia_manifiesta: "Urgencia manifiesta",
  infraestructura_alta_complejidad: "Infraestructura alta complejidad",
  valor_atipico_alto: "Valor atípico alto",
  objeto_difuso: "Objeto difuso",
  modalidad_propuesta_inconsistente: "Modalidad propuesta inconsistente",
};

const FIELD_LABELS = {
  need_description: "Descripción de la necesidad (mín. 30 caracteres)",
  object_description: "Objeto contractual (mín. 30 caracteres)",
  estimated_amount_cop: "Valor estimado en COP (mayor a cero)",
  consent_terms: "Aceptación de Términos y Política de Datos",
};

export default function PrevalidationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseDoc, setCaseDoc] = useState(null);
  const [prev, setPrev] = useState(null);
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reprevalidating, setReprevalidating] = useState(false);
  const [requestingN1, setRequestingN1] = useState(false);

  // Fase 12 — selector de variante inline
  const [variantSelected, setVariantSelected] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Fase 12 Sprint 2 — quota mensual + listado de versiones
  const [quota, setQuota] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedPrevId, setSelectedPrevId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: c }, prevRes, intakeRes, versionsRes, quotaRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/prevalidation`).catch((e) => ({ error: e })),
        api.get(`/cases/${id}/intake`).catch(() => null),
        api.get(`/cases/${id}/prevalidations`).catch(() => ({ data: { items: [] } })),
        api.get(`/me/quota`).catch(() => ({ data: null })),
      ]);
      setCaseDoc(c);
      if (intakeRes?.data) setIntake(intakeRes.data);
      if (prevRes?.error) {
        if (prevRes.error?.response?.status === 404) setPrev(null);
        else setError(formatApiError(prevRes.error?.response?.data?.detail));
      } else {
        setPrev(prevRes.data);
        setSelectedPrevId(prevRes.data?.id || null);
      }
      setVersions(versionsRes?.data?.items || []);
      setQuota(quotaRes?.data || null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar la prevalidación");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onReprevalidate = async () => {
    // Verificación de cuota antes de invocar el endpoint
    if (quota && !quota.unlimited && quota.remaining <= 0) {
      toast.error(
        `Has alcanzado el límite de ${quota.prevalidations_limit} prevalidaciones para ${quota.year_month}. Contacta a soporte para ampliar tu cuota.`,
      );
      return;
    }
    setReprevalidating(true);
    try {
      const { data } = await api.post(`/cases/${id}/re-prevalidate`);
      setPrev(data?.prevalidation);
      setCaseDoc(data?.case);
      toast.success("Prevalidación actualizada (nueva versión)");
      // Recargar lista de versiones + quota
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const msg = formatApiError(e?.response?.data?.detail) || "No se pudo re-ejecutar";
      if (status === 422 && /l[ií]mite|cuota|quota/i.test(msg)) {
        toast.error(msg);
        await load();
      } else if (status === 429) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setReprevalidating(false);
    }
  };

  const onRequestN1 = async () => {
    if (!variantSelected) {
      toast.error("Debes elegir una variante de Diagnóstico");
      return;
    }
    if (variantSelected === "auto" && !disclaimerAccepted) {
      toast.error("Debes aceptar el disclaimer para la variante automatizada");
      return;
    }
    setRequestingN1(true);
    try {
      const body = {
        variant: variantSelected,
        disclaimer_accepted: variantSelected === "auto" ? disclaimerAccepted : false,
      };
      // Fase 12 Sprint 2: si hay >1 prevalidación, enviar la elegida como base
      if (versions.length > 1 && selectedPrevId) {
        body.base_prevalidation_id = selectedPrevId;
      }
      await api.post(`/cases/${id}/request-nivel1`, body);
      toast.success("Solicitud de Diagnóstico registrada. Continúa al pago.");
      navigate(`/cases/${id}/payment`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo solicitar el Diagnóstico");
    } finally {
      setRequestingN1(false);
    }
  };

  if (loading) {
    return (
      <div className="container-prose py-12">
        <div className="border border-gray-200 p-8 text-sm text-gray-500">
          Cargando resultado de prevalidación…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-prose py-12">
        <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm" data-testid="prev-error">
          {error}
        </div>
      </div>
    );
  }

  if (!prev) {
    return (
      <div className="container-prose py-12">
        <div className="max-w-2xl mx-auto">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">
            ← Volver al detalle del caso
          </Link>
          <div className="mt-4 border border-gray-200 p-8 bg-gray-50" data-testid="prev-not-found">
            <h1 className="font-serif text-2xl text-gray-900 mb-2">Sin resultado</h1>
            <p className="text-sm text-gray-600 mb-4">
              Este caso aún no tiene una prevalidación ejecutada. Solicítala desde el panel.
            </p>
            <Button asChild className="rounded-sm">
              <Link to={`/cases/${id}`}>Volver al caso</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const r = prev.result;
  const styles = RESULT_STYLES[r] || RESULT_STYLES.posiblemente_apto;
  const flags = prev.complexity_flags || [];

  return (
    <div data-testid="prevalidation-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">
            ← Volver al detalle del caso
          </Link>
        </div>

        {/* Iter20 — Summary + Stepper para orientación */}
        <CaseSummaryCard caseDoc={caseDoc} showLink={false} />
        <div className="mb-4">
          <CaseFlowStepper
            caseId={id}
            state={caseDoc?.state || "nivel0_ready"}
            timeline={{ prevalidation_at: prev.created_at }}
          />
        </div>

        {/* Iter25 — Guía de auto-orientación del paso (self-work UX) */}
        <div className="mb-6">
          <FlowGuide level="n0" quota={quota} state={caseDoc?.state} caseId={id} />
        </div>

        {/* Iter25 — Timeline IA: "lo que la IA hizo en TU caso" */}
        <div className="mb-6">
          <AiActivityTimeline level="n0" artifact={prev} />
        </div>

        <div className="mb-8 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
            Prevalidación Explorador — gratuita
          </p>
          <h1 className="font-serif text-3xl text-gray-900 mb-4">
            Resultado: {styles.label}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span
              data-testid={`badge-${styles.testid}`}
              className={`inline-flex items-center gap-2 border px-3 py-1 text-[11px] uppercase tracking-widest font-medium ${styles.pill}`}
            >
              {styles.label}
            </span>
            {caseDoc && (
              <span className="text-gray-500">
                {formatCOP(caseDoc.valor_estimado_cop)} · {formatDate(caseDoc.updated_at)}
              </span>
            )}
          </div>
        </div>

        {/* Razón */}
        <div className={`border border-gray-200 border-l-4 ${styles.accent} bg-white p-6 mb-6`}>
          <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
            Análisis del clasificador
          </p>
          <p data-testid="prev-reason" className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
            {prev.reason}
          </p>
          {prev.meta?.model_used && (
            <p className="mt-3 text-[11px] text-gray-400">
              Modelo: {prev.meta.model_used}
              {prev.meta.latency_ms ? ` · ${prev.meta.latency_ms} ms` : ""}
            </p>
          )}
        </div>

        {/* Datos insuficientes */}
        {r === "datos_insuficientes" && (
          <>
            <div className="border border-gray-300 bg-gray-50 p-6 mb-6" data-testid="missing-data-block">
              <h3 className="font-serif text-lg text-gray-900 mb-3">Campos que debes completar</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {(prev.missing_minimum_data || []).map((field) => (
                  <li key={field} className="flex items-start gap-2" data-testid={`missing-${field}`}>
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>{FIELD_LABELS[field] || field}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                <Button asChild className="rounded-sm" data-testid="cta-complete-data">
                  <Link to={`/cases/${id}`}>Completar datos del caso</Link>
                </Button>
              </div>
            </div>
            <N0LeadCapture bucket="datos_insuficientes" caseId={id} defaultEmail={user?.email} />
          </>
        )}

        {/* Flags */}
        {flags.length > 0 && (
          <div className="border border-gray-200 bg-white p-6 mb-6" data-testid="flags-block">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">Banderas detectadas</p>
            <div className="flex flex-wrap gap-2">
              {flags.map((f) => (
                <span
                  key={f}
                  data-testid={`flag-${f}`}
                  className="inline-flex items-center gap-2 border border-gray-300 bg-gray-50 px-3 py-1 text-xs text-gray-700"
                >
                  {FLAG_LABELS[f] || f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Apto / Posiblemente apto → CTA Diagnóstico */}
        {prev.show_l1_cta && (
          <>
            {/* Fase 11.5 Sprint 1: ROI Calculator */}
            <ROICalculator
              valorContrato={caseDoc?.valor_estimado_cop || 0}
              modalidad={intake?.modalidad_propuesta || intake?.expected_modality || ""}
            />
            <div
              className="border-2 border-primary/30 bg-primary/[0.02] p-7 mb-6"
              data-testid="l1-offer-block"
            >
            <p className="text-[11px] uppercase tracking-widest text-primary font-medium mb-3">
              Siguiente paso
            </p>
            <h2 className="font-serif text-2xl text-gray-900 mb-2">
              Elige tu plan de Diagnóstico
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed mb-5">
              {prev.level1_offer_summary}
            </p>

            {/* Selector de variante */}
            <div className="grid md:grid-cols-2 gap-4 mb-5">
              <label
                className={`border-2 p-5 rounded-sm cursor-pointer transition bg-white ${variantSelected === "auto" ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400"}`}
                data-testid="variant-card-auto"
              >
                <input
                  type="radio"
                  name="variant_n1_prev"
                  value="auto"
                  checked={variantSelected === "auto"}
                  onChange={(e) => { setVariantSelected(e.target.value); setDisclaimerAccepted(false); }}
                  className="mr-2"
                  data-testid="variant-auto"
                />
                <span className="inline-block text-[9px] uppercase bg-amber-50 text-amber-800 border border-amber-300 px-2 py-1 mb-2">
                  Sin revisión humana
                </span>
                <div className="font-serif text-lg text-gray-900">Diagnóstico automatizado</div>
                <div className="font-serif text-2xl text-gray-900 my-2">{formatCOP(199000)}</div>
                <p className="text-xs text-gray-600 mt-2">Generación automática por IA. Entrega en 1–3 minutos. Más económico.</p>
              </label>

              <label
                className={`border-2 p-5 rounded-sm cursor-pointer transition bg-white ${variantSelected === "reviewed" ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400"}`}
                data-testid="variant-card-reviewed"
              >
                <input
                  type="radio"
                  name="variant_n1_prev"
                  value="reviewed"
                  checked={variantSelected === "reviewed"}
                  onChange={(e) => setVariantSelected(e.target.value)}
                  className="mr-2"
                  data-testid="variant-reviewed"
                />
                <span className="inline-block text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-2 py-1 mb-2">
                  Con revisión humana experta
                </span>
                <div className="font-serif text-lg text-gray-900">Diagnóstico con revisión humana</div>
                <div className="font-serif text-2xl text-gray-900 my-2">{formatCOP(489000)}</div>
                <p className="text-xs text-gray-600 mt-2">IA + revisión humana experta. Entrega en 24–48 horas hábiles. Mayor seguridad jurídica.</p>
              </label>
            </div>

            {variantSelected && (
              <div className="mb-5">
                <p className="text-[11px] uppercase tracking-widest text-gray-600 font-medium mb-2">Qué se entrega</p>
                <EntregablesPanel nivel={variantSelected === "auto" ? "diagnostico" : "diagnostico_revisado"} />
              </div>
            )}

            {variantSelected === "auto" && (
              <div className="border border-amber-300 bg-amber-50 p-4 mb-5">
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-1"
                    data-testid="disclaimer-auto-checkbox"
                  />
                  <span>
                    <strong>Entiendo y acepto:</strong> El documento será generado por IA sin revisión humana experta.
                    JustSapiens Procure NO garantiza la corrección legal del contenido. Asumo total responsabilidad por su uso.
                    Este documento no reemplaza el concepto de un abogado titulado.
                  </span>
                </label>
              </div>
            )}

            <Button
              onClick={onRequestN1}
              disabled={requestingN1 || !variantSelected || (variantSelected === "auto" && !disclaimerAccepted)}
              className="rounded-sm h-12 px-6"
              data-testid="cta-request-n1"
            >
              {requestingN1 ? "Procesando…" : "Continuar al pago"}
            </Button>

            {/* Iter18 — Aviso claro de opción de cuota gratuita disponible */}
            {quota && (quota.diagnoses_remaining ?? 0) > 0 ? (
              <div
                className="mt-4 border-2 border-emerald-300 bg-emerald-50/60 p-4 flex items-start gap-3"
                data-testid="quota-available-notice"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-emerald-100 border border-emerald-300 flex items-center justify-center">
                  <span className="font-serif text-lg text-emerald-800">★</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">
                    Tienes cuota gratuita disponible
                  </p>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    Te quedan <strong>{quota.diagnoses_remaining}</strong> de {quota.diagnoses_limit} cuotas
                    gratuitas de Diagnóstico este mes ({quota.year_month}).
                    Al continuar al pago, podrás elegir entre <strong>usar 1 cuota gratuita</strong> (a costo $0)
                    o <strong>pagar con MercadoPago/transferencia</strong>.
                  </p>
                  <p className="text-[11px] text-emerald-900/80 mt-2">
                    💡 Si usas tu cuota, el Diagnóstico se procesa automáticamente sin necesidad de comprobante.
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-[11px] text-gray-500">
                Al continuar serás llevado a la página de pago, donde podrás pagar con MercadoPago (tarjeta, PSE, Efecty) o por transferencia.
              </p>
            )}
          </div>
          </>
        )}

        {/* No apto */}
        {r === "no_apto" && (
          <>
            <div className="border border-rose-200 bg-rose-50/50 p-6 mb-6" data-testid="no-apto-block">
              <h3 className="font-serif text-lg text-rose-900 mb-2">Reformulación recomendada</h3>
              <p className="text-sm text-rose-800 mb-4">
                Tu caso requiere ajustes en el intake o consulta con un experto antes de avanzar a
                un diagnóstico.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="rounded-sm border-rose-300 text-rose-900" data-testid="cta-edit-case">
                  <Link to={`/cases/${id}`}>Editar datos del caso</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-sm border-rose-300 text-rose-900" data-testid="cta-contact-expert">
                  <a href="mailto:contacto@procure.justsapiens.com">Contactar a un experto</a>
                </Button>
              </div>
            </div>
            <N0LeadCapture bucket="no_apto" caseId={id} defaultEmail={user?.email} />
          </>
        )}

        {/* Out of scope */}
        {r === "out_of_scope" && (
          <>
            <div className="border border-indigo-200 bg-indigo-50/50 p-6 mb-6" data-testid="out-of-scope-block">
              <h3 className="font-serif text-lg text-indigo-900 mb-2">Fuera de alcance automático</h3>
              <p className="text-sm text-indigo-900 leading-relaxed mb-4">
                Tu caso pertenece a una categoría que requiere acompañamiento especializado
                (APP, concesión, régimen especial complejo, urgencia manifiesta u obra de alta
                complejidad). El flujo automatizado no aplica. Por favor contáctanos en{" "}
                <a href="mailto:contacto@procure.justsapiens.com" className="underline">
                  contacto@procure.justsapiens.com
                </a>{" "}
                para una propuesta personalizada.
              </p>
              <Button asChild variant="outline" className="rounded-sm border-indigo-300 text-indigo-900" data-testid="cta-out-of-scope-contact">
                <a href="mailto:contacto@procure.justsapiens.com">Solicitar contacto especializado</a>
              </Button>
            </div>
            <N0LeadCapture bucket="out_of_scope" caseId={id} defaultEmail={user?.email} />
          </>
        )}

        {/* Re-prevalidate */}
        <div className="mb-8">
          {/* Banner de quota mensual (Fase 12 Sprint 2) */}
          {quota && !quota.unlimited && (
            <div
              className={`border p-3 mb-4 text-sm ${quota.remaining <= 0 ? "border-rose-200 bg-rose-50 text-rose-900" : quota.remaining === 1 ? "border-amber-200 bg-amber-50 text-amber-900" : "border-gray-200 bg-gray-50 text-gray-700"}`}
              data-testid="quota-banner"
            >
              <span className="font-medium">Prevalidaciones gratuitas:</span>{" "}
              {quota.prevalidations_used} de {quota.prevalidations_limit} usadas este mes (
              {quota.year_month}). Te quedan <strong>{quota.remaining}</strong>.
              {quota.remaining <= 0 && (
                <span className="block mt-1 text-xs">
                  Has alcanzado el límite. Contacta a soporte para ampliar tu cuota.
                </span>
              )}
            </div>
          )}

          {/* Lista de versiones de prevalidación (si hay >1) */}
          {versions.length > 1 && (
            <div className="border border-gray-200 bg-white p-4 mb-4" data-testid="prevalidation-versions">
              <p className="text-[11px] uppercase tracking-widest text-gray-600 mb-2">
                Versiones de prevalidación ({versions.length})
              </p>
              <p className="text-xs text-gray-600 mb-3">
                Tu caso tiene varias prevalidaciones. Elige la que usarás como base para solicitar el Diagnóstico.
              </p>
              <div className="space-y-2 mb-2">
                {versions.map((v) => {
                  const isCurrent = v.is_current === true;
                  const isSelected = selectedPrevId === v.id;
                  return (
                    <label
                      key={v.id}
                      className={`flex items-start gap-3 border p-3 cursor-pointer rounded-sm ${isSelected ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`prevalidation-version-${v.version}`}
                    >
                      <input
                        type="radio"
                        name="base-prevalidation"
                        checked={isSelected}
                        onChange={() => setSelectedPrevId(v.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">Versión {v.version}</span>
                          {isCurrent && (
                            <span className="text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5">
                              Actual
                            </span>
                          )}
                          {v.intake_version_used && (
                            <span className="text-[9px] uppercase bg-gray-50 text-gray-700 border border-gray-300 px-1.5 py-0.5">
                              Borrador v{v.intake_version_used}
                            </span>
                          )}
                          {v.has_diagnosis && (
                            <span className="text-[9px] uppercase bg-indigo-50 text-indigo-800 border border-indigo-300 px-1.5 py-0.5">
                              Diagnosticada ({(v.diagnoses_generated || []).map((d) => `v${d.version}`).join(", ")})
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Resultado: <strong>{v.result}</strong> · {formatDate(v.created_at)}
                        </p>
                        {v.has_diagnosis && (
                          <p className="text-[10px] text-gray-500 mt-1">
                            Esta prevalidación ya fue usada para generar diagnóstico. Si pides un nuevo
                            diagnóstico sobre ella, se generará una nueva versión.
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Botón re-ejecutar */}
          {(r === "datos_insuficientes" || r === "no_apto" || r === "posiblemente_apto") && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={onReprevalidate}
                disabled={reprevalidating || (quota && !quota.unlimited && quota.remaining <= 0)}
                className="rounded-sm h-10"
                data-testid="cta-reprevalidate"
              >
                {reprevalidating
                  ? "Re-ejecutando…"
                  : quota && !quota.unlimited && quota.remaining <= 0
                  ? "Sin cuota disponible"
                  : "Re-ejecutar prevalidación"}
              </Button>
            </div>
          )}
        </div>

        <Disclaimer dataTestId="prevalidation-disclaimer" />
      </div>
    </div>
  );
}
